import { Component, ChangeDetectionStrategy, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { FooterComponent } from '../shared/footer/footer';
import { TranslationService, Language } from '../services/translation.service';

/**
 * Login Component
 *
 * Provides login form with two authentication methods:
 * - Azure AD (Microsoft SSO)
 * - Direct login (username/password)
 *
 * OAuth callbacks are handled by AuthCallback component at /auth/callback
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.html',
  standalone: true,
  imports: [FormsModule, FooterComponent, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Login implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly ts = inject(TranslationService);

  username = signal('');
  password = signal('');
  errorMessage = signal('');
  successMessage = signal('');
  isLoading = signal(false);
  retryTimer = signal(0);

  languages: Language[] = ['de', 'en', 'fr', 'it', 'nl'];

  ngOnInit(): void {
    // Check for error from OAuth flow or backend redirect
    this.route.queryParams.subscribe((params) => {
      if (params['msg']) {
        this.successMessage.set(params['msg']);
      }

      if (params['error'] === 'invalid_password') {
        this.errorMessage.set(this.ts.t('msg_password_wrong'));
        this.startRetryTimer();
      } else if (params['error'] === 'user_not_found') {
        this.errorMessage.set(this.ts.t('msg_username_unknown'));
        this.startRetryTimer();
      } else if (params['error'] === 'invalid_credentials') {
        this.errorMessage.set(this.ts.t('msg_login_failed'));
        this.startRetryTimer();
      } else if (params['error']) {
        this.errorMessage.set(params['error']);
      }
    });
  }

  private startRetryTimer(): void {
    this.retryTimer.set(5);
    const interval = setInterval(() => {
      this.retryTimer.update(val => val - 1);
      if (this.retryTimer() <= 0) {
        clearInterval(interval);
      }
    }, 1000);
  }

  onUsernameChange(value: string): void {
    this.username.set(value);
  }

  onPasswordChange(value: string): void {
    this.password.set(value);
  }

  async onLogin(): Promise<void> {
    this.errorMessage.set('');

    if (!this.username() || !this.password()) {
      this.errorMessage.set(this.ts.t('msg_empty_fields'));
      return;
    }

    console.log('🚀 Login.onLogin: Submitting credentials for', this.username());
    this.isLoading.set(true);

    try {
      await this.authService.submitLoginCredentials(this.username(), this.password());
      // Window will be redirected by the service after form submit
    } catch (error) {
      console.error('❌ Login.onLogin error:', error);
      this.errorMessage.set(this.ts.t('msg_login_failed'));
      this.isLoading.set(false);
    }
  }

  async onAzureLogin(): Promise<void> {
    this.errorMessage.set('');
    this.isLoading.set(true);

    try {
      const azureAuthUrl = this.authService.getAzureAuthorizationUrl();
      window.location.href = azureAuthUrl;
    } catch (error) {
      this.errorMessage.set(this.ts.t('msg_azure_failed'));
      this.isLoading.set(false);
    }
  }

  navigateToExtAuth(mode: string): void {
    this.router.navigate(['/ext-auth'], { queryParams: { mode } });
  }
}
